import axios from '@/lib/v2/axios';
import type { File } from '@/types/v2/file';

/** File-like object for uploads (DOM File or Blob with name/type) */
type UploadFile = { name: string; type: string };

/** Get presigned upload URL and key for a file (e.g. reservation documents). Upload file with PUT to url, then use key in API. */
export const getPresignedUploadUrl = async (
  file: UploadFile,
): Promise<{ url: string; key: string }> => {
  const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
  const response = await axios.post('/api/v2/files/presigned-upload-url', {
    file_name: fileName,
    file_type: file.type || 'application/octet-stream',
  });
  return { url: response.data.url, key: response.data.key };
};

export const uploadFileToPresignedUrl = async (
  url: string,
  file: Blob & { type?: string },
): Promise<void> => {
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) {
    throw new Error('Upload failed');
  }
};

export const getS3PresignedUrl = async (file: File) => {
  try {
    // Try v2 first, fallback to v1 if needed
    try {
      const response = await axios.get(`/api/v2/files/presigned-url`, {
        params: {
          fileId: file.id,
        },
      });
      return response.data.url;
    } catch (v2Error) {
      console.log('V2 endpoint failed, trying V1:', v2Error);
      // Fallback to v1 endpoint
      const response = await axios.get(`/api/v1/files/presigned-url`, {
        params: {
          fileId: file.id,
        },
      });
      return response.data.url;
    }
  } catch (error) {
    console.error('All presigned URL endpoints failed:', error);
    throw error;
  }
};

export const getS3PresignedUrlFromPath = async (filePath: string) => {
  try {
    // Try v2 first, fallback to v1 if needed
    try {
      const response = await axios.get(`/api/v2/files/presigned-url-from-path`, {
        params: {
          filePath: filePath,
          mobile: false,
        },
      });
      return response.data.url;
    } catch (v2Error) {
      console.log('V2 endpoint failed, trying V1:', v2Error);
      // Fallback to v1 endpoint
      const response = await axios.get(`/api/v1/files/presigned-url-from-path`, {
        params: {
          filePath: filePath,
          mobile: false,
        },
      });
      return response.data.url;
    }
  } catch (error) {
    console.error('All presigned URL endpoints failed:', error);
    throw error;
  }
};
